import { AuthSelectors, FirebaseSelectors, ObFlowService, RemoteConfigService } from '@web_opxp/features';
import { ELanguage, useTranslation } from '@web_opxp/language';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router';
import { HistoryService } from 'web_core_library';
import { AppSelectors } from '../app';
import { WELCOME_ANCHOR } from '../base/constants';
import * as ENV from '../base/env';
import isMobile from '../base/isMobile';
import { scrollToTop } from '../base/scrollToTop';
import { getProjectsUrl, getStudiesUrl, redirectMobileStore } from '../base/utils';
import BenefitsSection from '../components/onboarding/BenefitsSection';
import CommunitySection from '../components/onboarding/CommunitySection';
import DiscoverSection from '../components/onboarding/DiscoverSection';
import ExercisesSection from '../components/onboarding/ExercisesSection';
import GroupPackageSection from '../components/onboarding/GroupPackageSection';
import PartnersSection from '../components/onboarding/PartnersSection';
import ScienceSection from '../components/onboarding/ScienceSection';
import ScientificSection from '../components/onboarding/ScientificSection';
import TrustBannerSection from '../components/onboarding/TrustBannerSection';
import { isWelcomePage, welcomeHomeRoute, welcomeMascotRoute, welcomeVideoRoute } from '../routes';
import { UnityActions } from '../unity';
import * as FirebaseActions from './firebaseActions';
import HeroSection from './HeroSection';
import IntroVideoSection from './IntroVideoSection';
import MascotSection from './MascotSection';
import './WelcomePage.scss';

export interface IWelcomePageProps {
  focused: boolean;
  session?: string;
  unityPath?: string;
  onStartUnity: (preload?: boolean, basePath?: string) => void;
  onTopButtonHandler: () => void;
  getStartedFirebaseAction: () => void;
  learnMoreFirebaseAction: () => void;
}

export const WelcomePage: React.FC<IWelcomePageProps> = ({
  focused,
  session,
  unityPath,
  onStartUnity,
  onTopButtonHandler,
  getStartedFirebaseAction,
  learnMoreFirebaseAction,
}) => {
  const onGetStartedHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    getStartedFirebaseAction();
    scrollToTop();
    setTimeout(postScrollAction, 500);
  };
  const { pathname } = useLocation();

  const postScrollAction = () => {
    if (isWelcomePage(pathname)) {
      return !isMobile() ? ObFlowService.redirectToNextObStep(undefined, language, session) : redirectMobileStore();
    }
  };

  const onLearnMoreHandler = () => {
    learnMoreFirebaseAction();
  };

  const onTopButtonClick = (event: React.MouseEvent) => {
    if (!isMobile()) {
      onStartUnity(true, unityPath);
      onTopButtonHandler();
      return;
    }
    event.preventDefault();
    return redirectMobileStore();
  };

  // BW-2960 Skip intro view and go to the video
  useEffect(() => {
    if (pathname.endsWith('intro')) {
      HistoryService.redirectInternal('/welcome/video');
    }
  }, [pathname]);

  const { language } = useTranslation();

  const blogStudiesUrl = language ? getStudiesUrl(language) : '';
  const blogProjectsUrl = language ? getProjectsUrl(language) : '';
  return (
    <div className="welcome-page" id={WELCOME_ANCHOR}>
      <Routes>
        <Route path={welcomeMascotRoute} element={<MascotSection />} />
        <Route path={welcomeVideoRoute} element={<IntroVideoSection />} />
        <Route path={welcomeHomeRoute} element={<HeroSection onTopButtonHandler={onTopButtonClick} />} />
      </Routes>
      <TrustBannerSection />
      {focused ? null : (
        <>
          <ExercisesSection onGetStarted={onGetStartedHandler} />
          <PartnersSection blogUrl={blogStudiesUrl} onLearnMore={onLearnMoreHandler} />
          {language === ELanguage.German && <GroupPackageSection onLearnMore={onLearnMoreHandler} />}
          <ScienceSection
            blogUrl={blogProjectsUrl}
            onLearnMore={onLearnMoreHandler}
            bannerDirection={language === ELanguage.German ? 'right' : 'left'}
          />
          <CommunitySection
            onGetStarted={onGetStartedHandler}
            bannerDirection={language === ELanguage.German ? 'left' : 'right'}
          />
          <BenefitsSection />
          <ScientificSection />
          <DiscoverSection onGetStarted={onGetStartedHandler} />
        </>
      )}
    </div>
  );
};

export const mapStateToProps = (state: any) => {
  const focused = AppSelectors.isFocusedMode(state);
  const session = AuthSelectors.getToken(state);
  let unityPath: string | undefined;
  const firebaseLoaded = FirebaseSelectors.isReady(state);
  if (firebaseLoaded) {
    unityPath = RemoteConfigService.getStringConfigValue(ENV.RemoteConfigUnity, ENV.UnityPath);
  }
  return { focused, session, unityPath };
};

export const mapDispatchToProps = {
  onStartUnity: UnityActions.initUnity,
  onTopButtonHandler: FirebaseActions.firebaseTopButtonClickAction,
  getStartedFirebaseAction: FirebaseActions.firebaseGetStartedClickAction,
  learnMoreFirebaseAction: FirebaseActions.firebaseLearnMoreClickAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(WelcomePage);
